import type { Hotel } from '../interfaces/hotel'

/**
 * Opciones de los filtros de la lista de hoteles, armadas con lo que trajo la
 * búsqueda. Todo corre en el navegador: cambiar un filtro no vuelve a buscar.
 */
export interface FilterOption {
	key: string
	label: string
	count: number
}

/**
 * Margen de los popovers de filtros contra el riel flotante del borde derecho
 * (FloatingRail): 46px en móvil y círculos de 56px a 20px del borde en escritorio.
 */
export const RAIL_COLLISION_PADDING = {
	top: 16,
	right: 80,
	bottom: 16,
	left: 16,
}

export type CancellationFilter = 'all' | 'refundable' | 'non_refundable'

/**
 * Llave para comparar textos que llegan distinto según el proveedor: Amadeus
 * manda «CANCUN» y Hotelbeds «Cancún». Sin acentos, minúsculas y un solo espacio.
 */
export function normalizeKey(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim()
}

// En español los artículos, preposiciones y conjunciones van en minúscula salvo al
// inicio: «San Nicolás de los Garza», no «San Nicolás De Los Garza».
const LOWERCASE_WORDS = new Set([
	'de',
	'del',
	'la',
	'las',
	'los',
	'el',
	'y',
	'e',
	'en',
	'a',
])

/** «PLAYA DEL CARMEN» → «Playa del Carmen»; lo que ya viene capitalizado se respeta. */
function displayLabel(value: string): string {
	const trimmed = value.trim()
	if (trimmed !== trimmed.toUpperCase()) return trimmed

	return trimmed
		.toLowerCase()
		.split(/(\s+)/)
		.map((word, index) =>
			index > 0 && LOWERCASE_WORDS.has(word) ?
				word
			:	word.replace(
					/(^|-)(\p{L})/gu,
					(_, sep: string, letter: string) => sep + letter.toUpperCase()
				)
		)
		.join('')
}

export function cityKey(hotel: Hotel): string | null {
	return hotel.city ? normalizeKey(hotel.city) : null
}

/** La zona viene de un catálogo fijo del back, así que su id ya es una llave estable. */
export function areaKey(hotel: Hotel): string | null {
	return hotel.areaId != null && hotel.areaName ? String(hotel.areaId) : null
}

/**
 * La cadena se agrupa por nombre cuando lo hay (catálogo o proveedor) y, si no,
 * por el código de 2 letras de Amadeus. Hotelbeds no manda cadena.
 */
export function chainKey(hotel: Hotel): string | null {
	if (hotel.chainName) return `name:${normalizeKey(hotel.chainName)}`
	if (hotel.chainCode) return `code:${hotel.chainCode.toUpperCase()}`
	return null
}

/** Una cadena de una sola palabra corta son siglas: «IHG», «NH», «AC», no «Ihg». */
function chainLabel(hotel: Hotel): string {
	if (hotel.chainName) {
		const name = hotel.chainName.trim()
		return /^\p{L}{1,4}$/u.test(name) ? name.toUpperCase() : displayLabel(name)
	}
	return `Cadena ${hotel.chainCode?.toUpperCase()}`
}

/**
 * Arma las opciones de un filtro de opción múltiple. Las etiquetas salen de TODOS
 * los hoteles (con la llave normalizada, «CANCUN» y «Cancún» caen en la misma
 * opción), pero los conteos salen de `countIn`: los hoteles que pasan los DEMÁS
 * filtros. Así, al elegir una cadena, Municipio sólo ofrece donde esa cadena tiene
 * hoteles. Una opción que se queda en 0 desaparece, salvo que esté elegida: si no,
 * no habría cómo quitarla.
 */
function buildOptions(
	hotels: Hotel[],
	countIn: Hotel[],
	selected: string[],
	keyOf: (hotel: Hotel) => string | null,
	labelOf: (hotel: Hotel) => string
): FilterOption[] {
	const options = new Map<string, FilterOption>()

	for (const hotel of hotels) {
		const key = keyOf(hotel)
		if (key && !options.has(key)) {
			options.set(key, { key, label: labelOf(hotel), count: 0 })
		}
	}

	for (const hotel of countIn) {
		const key = keyOf(hotel)
		const option = key ? options.get(key) : undefined
		if (option) option.count++
	}

	return [...options.values()]
		.filter((option) => option.count > 0 || selected.includes(option.key))
		.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'es'))
}

export function chainOptions(
	hotels: Hotel[],
	countIn: Hotel[] = hotels,
	selected: string[] = []
): FilterOption[] {
	return buildOptions(hotels, countIn, selected, chainKey, chainLabel)
}

export function cityOptions(
	hotels: Hotel[],
	countIn: Hotel[] = hotels,
	selected: string[] = []
): FilterOption[] {
	return buildOptions(hotels, countIn, selected, cityKey, (hotel) =>
		displayLabel(hotel.city ?? '')
	)
}

export function areaOptions(
	hotels: Hotel[],
	countIn: Hotel[] = hotels,
	selected: string[] = []
): FilterOption[] {
	return buildOptions(
		hotels,
		countIn,
		selected,
		areaKey,
		(hotel) => hotel.areaName ?? ''
	)
}

/**
 * Cómo se lee la tarifa listada (la más barata del hotel), con la misma regla que el
 * detalle (`cancellationStatus`): sólo `free_until` cancela sin costo; un plazo que
 * ya venció para el cliente cuenta como no reembolsable. Con un back que todavía no
 * manda el estado, se cae al booleano `refundable`.
 */
export function listedCancellation(
	hotel: Hotel
): 'free' | 'non_refundable' | 'unknown' {
	switch (hotel.cancellationStatus) {
		case 'free_until':
			return 'free'
		case 'non_refundable':
		case 'cutoff_passed':
			return 'non_refundable'
		case 'unknown':
			return 'unknown'
	}
	if (hotel.refundable === true) return 'free'
	if (hotel.refundable === false) return 'non_refundable'
	return 'unknown'
}

export function matchesCancellation(
	hotel: Hotel,
	filter: CancellationFilter
): boolean {
	if (filter === 'all') return true
	const listed = listedCancellation(hotel)
	return filter === 'refundable' ?
			listed === 'free'
		:	listed === 'non_refundable'
}

/** `unknown`: los que no entran en ninguna de las dos, para que los números cuadren. */
export function cancellationCounts(
	hotels: Hotel[]
): Record<Exclude<CancellationFilter, 'all'> | 'unknown', number> {
	const counts = { refundable: 0, non_refundable: 0, unknown: 0 }
	for (const hotel of hotels) {
		const listed = listedCancellation(hotel)
		if (listed === 'free') counts.refundable++
		else if (listed === 'non_refundable') counts.non_refundable++
		else counts.unknown++
	}
	return counts
}
